import React, {ReactNode} from 'react';
import {Link} from 'react-router-dom';

interface BaseButtonProps {
    type: 'primary' | 'primary-outline' | 'primary-light' | 'default' | 'default-light' | 'default-light-active' | 'danger' | 'danger-outline';
    elementType: 'link' | 'button' | 'a';
    attributes: any;
    children?: ReactNode;
    text?: ReactNode;
    disabled?: boolean;
    size?: 'sm' | 'md' | 'lg'; // Ajouter la propriété size
    color?: string;
}

export type ButtonProps = BaseButtonProps & (
    | { elementType: 'link'; attributes: { to: string; className?: string; [key: string]: any } }
    | { elementType: 'button'; attributes: { onClick?: () => void; type?: 'button' | 'submit' | 'reset'; [key: string]: any } }
    | { elementType: 'a'; attributes: { href: string; className?: string; [key: string]: any } }
    );

export default function Button({
                                   children,
                                   text,
                                   type,
                                   elementType,
                                   attributes,
                                   disabled = false,
                                   size = 'md',
                                   color = '#006F68', // Couleur par défaut
                               }: ButtonProps) {
    // Classes spécifiques aux tailles
    const sizeClasses = {
        sm: 'px-2 py-1 text-xs',
        md: 'px-3 py-2 text-sm',
        lg: 'px-4 py-3 text-base',
    };

    // Gestion des classes
    const baseClasses = `inline-flex items-center justify-center font-light focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600 ${sizeClasses[size]}`;

    // Ajout des classes pour l'état disabled
    const disabledClasses = disabled ? 'opacity-50 cursor-not-allowed' : '';

    const {
        style: userStyle,
    } = attributes as { style?: React.CSSProperties; className?: string; [key:string]: any };

    // 2) Ton style « internal » (créé par --btn-color)
    const internalStyle = { '--btn-color': color } as React.CSSProperties;

    // 3) Fusionne les deux
    const style = {
        ...internalStyle,
        ...userStyle
    };

    const typeClasses =
        type === 'primary'
            ? 'btn-primary-gradient text-white rounded-md'
            : type === 'primary-outline'
                ? 'text-[var(--btn-color)] ring-[var(--btn-color)] bg-white ring-1 ring-inset rounded-md '
                : type === 'primary-light'
                    ? 'text-[var(--btn-color)] bg-white border-0 rounded-md '
                    : type === 'danger'
                        ? 'bg-danger-color text-white hover:bg-red-500 rounded-md '
                        : type === 'danger-outline'
                            ? 'ring-1 ring-inset ring-danger-color text-danger-color rounded-md '
                            : type === 'default-light'
                                ? 'text-text-color ring-0 border-0 disabled:text-gray-300 disabled:cursor-not-allowed'
                                : type === 'default-light-active'
                                    ? 'text-text-color ring-0 border-b-2 border-main-color disabled:text-gray-300 disabled:cursor-not-allowed'
                                    : type === 'default'
                                        ? 'bg-white text-text-color ring-1 ring-inset ring-gray-300 hover:bg-gray-50 rounded-md '
                                        : 'bg-white text-text-color ring-1 ring-inset ring-gray-300 hover:bg-gray-50 rounded-md ';
    const classes = `${baseClasses} ${typeClasses} ${disabledClasses} ${attributes.className || ''}`;

    // Fonction pour gérer le clic en fonction de l'état disabled
    const handleClick = (event: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement, MouseEvent>) => {
        if (disabled) {
            event.preventDefault();
            return;
        }
        if (attributes.onClick) {
            attributes.onClick();
        }
    };
    const commonProps = {
        ...attributes,
        className: classes,
        style,
        onClick: handleClick,
        'aria-disabled': disabled,
        tabIndex: disabled ? -1 : 0,
    };


    switch (elementType) {
        case 'link':
            return (
                <Link {...commonProps}>
                    {text ? text : children}
                </Link>
            );
        case 'button':
            return (
                <button
                    disabled={disabled}
                    type={attributes.type || 'button'}
                    {...attributes}
                    style={style}
                    className={classes}
                >
                    {text ? text : children}
                </button>
            );
        case 'a':
            return (
                <a {...commonProps}>
                    {text ? text : children}
                </a>
            );
        default:
            return null; // ou un élément par défaut
    }
}
